/**
 * Sentry — web-customer client-side error capture.
 *
 * Mounted once from `main.tsx` BEFORE React renders. After mount,
 * any unhandled exception or unhandled promise rejection in the
 * customer portal lands in Sentry with a session replay attached
 * (so the on-call engineer can see what the customer clicked).
 *
 * DSN handling:
 *   The Sentry DSN is project-public (it's safe to embed in client
 *   code) but we keep it in Vite env so the prod / staging / test
 *   builds each get their own. Missing DSN → no-op (so `npm run dev`
 *   doesn't spam your project with dev-noise events).
 *
 * What we surface vs. what we hide:
 *   - Surface: stack trace, current route, user uid (NOT phone/email)
 *   - Hide:    request bodies, cookies, anything resembling card /
 *              UPI / OTP / contact data. The customer portal never
 *              handles raw card data (Razorpay Checkout is iframed),
 *              but be paranoid anyway — Sentry retains 90 days.
 */
import * as Sentry from '@sentry/react';

let initialised = false;

export function initWebSentry(): void {
  if (initialised) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    console.warn(
      '[sentry] VITE_SENTRY_DSN not set — error monitoring is disabled.',
    );
    initialised = true;
    return;
  }

  Sentry.init({
    dsn,
    // `browserTracingIntegration` instruments fetch/XHR + route
    // changes so you can see slow page loads + how customers move
    // between Plans → Billing. `replayIntegration` captures the
    // DOM mutation stream of a session that errored — invaluable
    // for "the upgrade button didn't work" tickets.
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Don't replay sessions that didn't error (saves quota).
        // Mask all text and inputs by default — we don't want phone
        // numbers / OTP digits in the replay.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1, // 10% of pageloads
    replaysSessionSampleRate: 0, // never replay non-error sessions
    replaysOnErrorSampleRate: 1.0, // 100% of errored sessions
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE ?? undefined,
    sendDefaultPii: false,
  });
  initialised = true;
}

/** Attach uid to all subsequent events. Call this from the auth
 *  provider's `onAuthStateChanged` callback. Passing `null` on
 *  sign-out clears it. */
export function setSentryUser(uid: string | null): void {
  if (!initialised) return;
  Sentry.setUser(uid ? { id: uid } : null);
}

/** Manually report a handled error with context. Use sparingly —
 *  the global handlers cover unhandled errors automatically. */
export function captureWebError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!initialised) {
    console.error('[error]', error, context);
    return;
  }
  Sentry.withScope((scope) => {
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        scope.setExtra(key, value);
      }
    }
    Sentry.captureException(error);
  });
}
